/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { LocaleContent, resolveCopy, translate } from "common/utils/i18n"
import { storageHelper } from "utils/storage"

/**
 * Loads a generated locale bundle and hands its content to `callback`. A bundle
 * that is missing or fails to load leaves the caller on the content it already
 * has — the app still has to render.
 *
 * This is deliberately the only `import()` of `public/locales/*` in the app:
 * webpack builds one lazy chunk group per call site, so a second copy of this
 * function would emit a duplicate chunk for every locale. Both LocaleContext
 * providers — `HomeContainer` and `components/Locale/LocaleProvider` — share
 * it for that reason, as does `CrashFallback`'s `critical` variant, which
 * needs locale content but renders outside every provider. Reuse this rather
 * than adding another `import(\`.../public/locales/${locale}.ts\`)` anywhere
 * else in the app.
 */
export const fetchLocale = async (
  locale: string,
  callback: (_: LocaleContent) => void,
  onError?: () => void,
) => {
  try {
    const localModule = await import(`../../public/locales/${locale}.ts`)
    callback(localModule.default)
  } catch (e) {
    // don't care the error — but let callers that can't stay silent know
    onError?.()
  }
}

/**
 * The generic BE 403 message ("Forbidden") is not localized; resolves the
 * unauthorized-access copy from the locale bundle instead, falling back to the
 * BE message until the Phrase key is published and pulled. Returns null for
 * any other status/message so callers keep their existing handling. Shared by
 * both request layers — the redux showError middleware and the RTK Query base
 * query — so the toast title is localized no matter which one surfaces it.
 * A bundle that fails to load resolves with the BE message, so the toast still
 * shows instead of being swallowed.
 */
export const resolveGenericForbiddenMessage = (
  status: number | undefined,
  message: unknown,
): Promise<string> | null => {
  if (
    status !== 403 ||
    typeof message !== "string" ||
    message.trim().toLowerCase() !== "forbidden"
  ) {
    return null
  }
  return new Promise(resolve => {
    fetchLocale(
      storageHelper.getLang(),
      localeContent => {
        resolve(
          resolveCopy(
            translate(localeContent, "common.unauthorizedAccess"),
            message,
          ),
        )
      },
      () => resolve(message),
    )
  })
}
